import React, { useEffect, useState } from 'react';
import { Box, Button, InputAdornment, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import SearchInput from '../Common/SearchInput';
import StyledBackButton from '../Common/StyledBackButton';
import CustomModal from '../Common/CustomModal';
import {
  closeAddActivityModalOnSuccess,
  setCreateFolderModalOpen,
} from '../../redux/activityLibrary/activityLibrarySlice';
import CreateRenameFolderModal from './CreateRenameFolderModal/CreateRenameFolderModal';
import { useAppSelector } from '../../redux/hooks';
import CustomizeActivityModal from './CustomizeActivityModal';
import { ACTIVITY_LIBRARY_ROUTE } from '../../utils/routes';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

const StyledButton = styled(Button)(({ disabled }) => ({
  type: 'submit',
  fullWidth: true,
  variant: 'contained',
  borderRadius: 15,
  padding: '12px 6px !important',
  fontSize: '17px',
  width: '100%',
  gap: '4px',
  background: disabled
    ? 'var(--Accent-Light-Steel-Blue, #EEE) !important'
    : 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
  color: 'var(--Accent-White, #FFF) !important',
  '&:hover': {
    background: disabled
      ? 'var(--Accent-Light-Steel-Blue, #EEE) !important'
      : 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
  },
  '& span': {
    margin: '0 2px',
  },
}));

interface IActivityLibraryTopHeaderProps {
  searchHandler: (_value: React.ChangeEvent<HTMLInputElement>) => void;
  searchText: string;
  isSingleFolder?: boolean;
  isTrash?: boolean;
}

function ActivityLibraryTopHeader({
  searchHandler,
  searchText,
  isSingleFolder,
  isTrash,
}: IActivityLibraryTopHeaderProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const buttonStartIcon = '/assets/icons/add-circle-button.svg';
  const theme = useTheme();
  const location = useLocation();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const [isModalOpen, setModalOpen] = useState(false);
  const { isCreateFolderModalOpen, isAddActivitySuccess } = useAppSelector(
    (state) => state.activityLibrary
  );

  const closeModel = () => {
    setModalOpen(false);
  };

  const handleCreateFolder = () => {
    dispatch(setCreateFolderModalOpen(true));
  };

  const handleDismissCreateFolder = () => {
    dispatch(setCreateFolderModalOpen(false));
  };

  useEffect(() => {
    if (isAddActivitySuccess) {
      closeModel();
      dispatch(closeAddActivityModalOnSuccess());
    }
  }, [isAddActivitySuccess]);

  return (
    <>
      <Box
        bgcolor="#fff"
        width="100%"
        display="flex"
        p="20px 24px"
        gap={2}
        mt={location.pathname === ACTIVITY_LIBRARY_ROUTE ? '28px' : ''}
        boxShadow={theme.borderStyles.cardBorders}
        borderRadius="10px"
        justifyContent="space-between"
      >
        <Box
          display="flex"
          // justifyContent="flex-start"
          flex={isSingleFolder ? '8' : '7'}
        >
          <SearchInput
            placeholder={`${t('common.search')}...`}
            onChange={searchHandler}
            value={searchText}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src="/assets/icons/magnifer.svg" alt="search" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {!isSingleFolder &&
          location.pathname === ACTIVITY_LIBRARY_ROUTE &&
          checkPermissions(UserPermissionType.ACTIVITY_LIBRARY_MODULE_CREATE_ACTIVITY_LIBRARY) && (
            <Box
              display="flex"
              justifyContent="center"
              flex={1.5}
              flexDirection="column"
              alignItems="center"
            >
              <StyledBackButton
                onClick={handleCreateFolder}
                startIcon={<img src="/assets/icons/add-folder.svg" alt="add-folder" />}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{t('activityLibrary.filled.newFolder')}</span>
              </StyledBackButton>
            </Box>
          )}
        {!isTrash &&
          location.pathname === ACTIVITY_LIBRARY_ROUTE &&
          checkPermissions(UserPermissionType.ACTIVITY_LIBRARY_MODULE_CREATE_ACTIVITY_LIBRARY) && (
            <Box
              display="flex"
              justifyContent="center"
              width={{ xs: '160px', md: '200px' }}
              flexDirection="column"
              alignItems="center"
            >
              <StyledButton
                onClick={() => setModalOpen(true)}
                startIcon={<img src={buttonStartIcon} alt="buttonStartIcon" />}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 17,
                    fontWeight: '500',
                    padding: '0 2px 0 0',
                  }}
                >
                  {t('activityLibrary.filled.createNewActivity')}
                </Typography>
              </StyledButton>
            </Box>
          )}
      </Box>
      {isModalOpen && (
        <CustomModal isOpen={isModalOpen} xs={11}>
          <CustomizeActivityModal closeModel={closeModel} isEditMode={false} />
        </CustomModal>
      )}

      <CreateRenameFolderModal
        isOpen={isCreateFolderModalOpen}
        isCreateFolder
        initialFolderName=""
        handleDismiss={handleDismissCreateFolder}
      />
    </>
  );
}

export default ActivityLibraryTopHeader;
